"use client";

import React, { useEffect, useState } from "react";
import {
  Award,
  BarChart3,
  Bell,
  Clock,
  Crown,
  FileText,
  Gamepad2,
  LayoutGrid,
  Link as LinkIcon,
  MessageCircle,
  Settings,
  Swords,
  Target,
  Trophy,
  Users,
  Video,
  Zap,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useTranslations } from "next-intl";

import { Badge } from "@/shared/ui/data-display/badge";
import { Meteors } from "@/shared/ui/meteors";
import { AnimatedDragonLogo } from "@/shared/ui/animated-dragon-logo";

interface HeroSectionProps {
  hideFeaturesGrid?: boolean;
  quickActionsSlot?: React.ReactNode;
  /**
   * Главная кнопка героя. Приходит функцией, а не готовым узлом, потому что её
   * оформление зависит от фона (showLightText) — он известен только здесь.
   */
  ctaSlot?: (opts: { className: string; style: React.CSSProperties }) => React.ReactNode;
}

export function HeroSection({ hideFeaturesGrid, quickActionsSlot, ctaSlot }: HeroSectionProps = {}) {
  const t = useTranslations("Hero");
  const { resolvedTheme } = useTheme();
  // KAN-591: до монтирования тему не знаем — и знать не должны. next-themes
  // читает её из localStorage, поэтому на клиенте resolvedTheme известен уже в
  // первом рендере, а на сервере он undefined. Ниже тема управляет НАЛИЧИЕМ
  // элементов (Meteors), так что расхождение — это не
  // разные классы, а разное дерево: ровно то, на что React отвечает ошибкой
  // #418. В GlitchTip это 250+ событий на / и /rosters.
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Первый клиентский рендер обязан повторить серверный: тема применяется со
  // следующего, уже после гидратации. Фоновые слои декоративны — задержка в
  // один кадр незаметна, а несовпадение дерева ломало гидратацию всей страницы.
  const isDark = mounted && resolvedTheme === "dark";

  // Вход блоков — CSS-класс .gl-enter (globals.css), а не framer-motion:
  // initial={{opacity:0}} у framer уезжает в SSR-разметку инлайновым стилем, и
  // до гидратации первый экран стоит пустым. CSS играет с первого кадра.
  // Сетка фич появляется ОДНОВРЕМЕННО (общая задержка, без stagger) — так же,
  // как было после dev_task fix-header-landing-repeating-animations.
  const enterUp = (delaySeconds: number) =>
    ({
      "--gl-enter-from": "translateY(30px)",
      "--gl-enter-delay": `${delaySeconds}s`,
    }) as React.CSSProperties;

  const ttBody = { fontFamily: "var(--font-body), sans-serif" } as const;

  const rowStartClasses = [
    "lg:row-start-3",
    "lg:row-start-4",
    "lg:row-start-5",
    "lg:row-start-6",
    "lg:row-start-7",
    "lg:row-start-8",
    "lg:row-start-9",
    "lg:row-start-10",
    "lg:row-start-11",
  ] as const;

  const organizerFeatures: Array<{
    key: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    comingSoon?: boolean;
  }> = [
    { key: "brackets", icon: LayoutGrid, color: "text-blue-500" },
    { key: "draftRooms", icon: FileText, color: "text-fuchsia-500" },
    { key: "teams", icon: Settings, color: "text-purple-500" },
    { key: "notifications", icon: Bell, color: "text-emerald-500" },
    { key: "formats", icon: Trophy, color: "text-amber-500" },
    { key: "discordBot", icon: MessageCircle, color: "text-indigo-500" },
    { key: "analytics2", icon: BarChart3, color: "text-cyan-500" },
    { key: "lobby", icon: Target, color: "text-red-500", comingSoon: true },
    { key: "streaming", icon: Video, color: "text-pink-500", comingSoon: true },
  ];

  const playerFeatures: Array<{
    key: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    comingSoon?: boolean;
  }> = [
    { key: "realtime", icon: Zap, color: "text-green-500" },
    { key: "gameStats", icon: Gamepad2, color: "text-blue-500" },
    { key: "tauriApp", icon: LayoutGrid, color: "text-violet-500" },
    { key: "leaderboard", icon: Award, color: "text-amber-500" },
    { key: "riotAccount", icon: LinkIcon, color: "text-red-500" },
    { key: "speed", icon: Clock, color: "text-yellow-500" },
    { key: "community", icon: Users, color: "text-teal-500" },
    { key: "stats", icon: BarChart3, color: "text-orange-500" },
    { key: "draftParticipation", icon: FileText, color: "text-fuchsia-500" },
  ];

  // Фоновые темы (KAN-1247) убраны: светлый текст — только в тёмной теме.
  const showDarkHeader = isDark;
  const showLightText = isDark;

  return (
    <section className="relative -mt-[76px] pt-[76px] sm:-mt-[88px] sm:pt-[88px] overflow-x-hidden pb-20 lg:pb-32">
      {/* KAN-297: обёртка статична — opacity:0-старт на всём блоке прятал LCP-текст.
          Входные анимации остаются на логотипе и фичах (itemVariants ниже). */}
      <div
        className={`relative -mt-[76px] sm:-mt-[88px] mb-8 pb-12 sm:pb-16 w-screen -ml-[50vw] left-1/2 ${
          !showDarkHeader ? "bg-background" : ""
        }`}
        style={
          showDarkHeader ? { backgroundColor: "transparent" } : undefined
        }
      >
        {isDark && (
          <div className="absolute inset-x-0 -top-[240px] bottom-0 pointer-events-none z-0">
            <Meteors className="inset-0" />
          </div>
        )}

        <div className="container mx-auto px-[50px] relative z-10 pt-[100px] sm:pt-[112px]">
          <div className="mx-auto text-center">
            <div
              className={`gl-enter flex justify-center mb-8 ${showLightText ? "text-white" : "text-foreground"}`}
              style={enterUp(0.1)}
            >
              <AnimatedDragonLogo className="w-48 h-48 sm:w-64 sm:h-64 lg:w-80 lg:h-80" />
            </div>

            <div
              className={`space-y-3 mx-auto mt-8 ${
                showLightText ? "text-white" : "text-foreground"
              }`}
            >
              {/* SEO: видимый заголовок скрыт по просьбе владельца, но без <h1>
                  поисковик не понимает, о чём страница. Заголовок остаётся только
                  для поисковиков и скринридеров. */}
              <h1 className="sr-only">{t("srTitle")}</h1>
              {/* ЗАГОЛОВОК И ОПИСАНИЕ СКРЫТЫ ПО ПРОСЬБЕ ПОЛЬЗОВАТЕЛЯ
              <motion.h1
                className={`mb-0 text-4xl font-bold tracking-tight sm:text-6xl lg:text-7xl ${
                  showLightText ? "text-white" : "text-foreground"
                }`}
                variants={itemVariants}
                style={ttBody}
              >
                GoodLuck
              </motion.h1>
              <p
                className={`text-sm sm:text-base lg:text-lg ${
                  showLightText ? "text-white/80" : "text-muted-foreground"
                }`}
                style={ttBody}
              >
                Платформа для организации турниров нового поколения.
              </p>
              */}
              {/* Раньше здесь висела кнопка «Скачать приложение» на /download. Убрана
                  намеренно: десктопное приложение пока не для широкой раздачи. Что
                  показывать вместо неё, решает страница — слотом (ctaSlot). */}
              {ctaSlot ? (
                <div className="mt-4">
                  {ctaSlot({
                    className: `inline-flex items-center gap-2 rounded-lg px-6 py-3 text-base font-medium transition-colors cursor-pointer ${
                      showLightText
                        ? "bg-white text-black hover:bg-white/90"
                        : "bg-primary text-primary-foreground hover:bg-primary/90"
                    }`,
                    style: ttBody,
                  })}
                </div>
              ) : null}
            </div>
          </div>
          {quickActionsSlot ? (
            <div className="mt-10 w-full max-w-6xl mx-auto text-left">{quickActionsSlot}</div>
          ) : null}
        </div>
      </div>

      {!hideFeaturesGrid && (
      <div className="container mx-auto px-[50px] relative">
        <div className="mx-auto max-w-4xl rounded-2xl bg-black/6 dark:bg-white/4 px-6 py-5 lg:px-8 lg:py-8">
          <div className="relative grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-[auto_1.5rem_repeat(9,auto)] lg:items-start gap-x-0 gap-y-5 lg:gap-x-6 lg:gap-y-5">
            <div
              className="gl-enter flex items-start gap-3 order-1 lg:order-0 lg:col-start-1 lg:row-start-1 lg:pr-3 relative z-10"
              style={enterUp(0.1)}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10 shrink-0">
                <Crown className="h-4.5 w-4.5 text-amber-500" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold mb-0.5" style={ttBody}>
                  {t("features.organizers.title")}
                </h2>
                <p className="text-sm text-muted-foreground max-w-[32ch]" style={ttBody}>
                  {t("features.organizers.subtitle")}
                </p>
              </div>
            </div>

            <div
              className="gl-enter flex items-start gap-3 order-3 lg:order-0 lg:col-start-2 lg:row-start-1 lg:pl-3 relative z-10"
              style={enterUp(0.1)}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 shrink-0">
                <Swords className="h-4.5 w-4.5 text-emerald-500" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold mb-0.5" style={ttBody}>
                  {t("features.players.title")}
                </h2>
                <p className="text-sm text-muted-foreground max-w-[32ch]" style={ttBody}>
                  {t("features.players.subtitle")}
                </p>
              </div>
            </div>

              {organizerFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.key}
                  className={`gl-enter flex items-start gap-3 order-2 lg:order-0 lg:col-start-1 lg:pr-3 ${rowStartClasses[i]} relative z-10`}
                  style={enterUp(0.1)}
                >
                  <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${f.color}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-medium" style={ttBody}>
                        {t(`features.organizers.items.${f.key}.title`)}
                      </h3>
                      {f.comingSoon && (
                        <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                          Soon
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5 max-w-[36ch]" style={ttBody}>
                      {t(`features.organizers.items.${f.key}.desc`)}
                    </p>
                  </div>
                </div>
              );
            })}

            {playerFeatures.map((f, i) => (
              <div
                key={f.key}
                className={`gl-enter flex items-start gap-3 order-4 lg:order-0 lg:col-start-2 lg:pl-3 ${rowStartClasses[i]} relative z-10`}
                style={enterUp(0.1)}
              >
                <f.icon className={`h-5 w-5 mt-0.5 shrink-0 ${f.color}`} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-medium" style={ttBody}>
                      {t(`features.players.items.${f.key}.title`)}
                    </h3>
                    {f.comingSoon && (
                      <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                        Soon
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5 max-w-[36ch]" style={ttBody}>
                    {t(`features.players.items.${f.key}.desc`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}
    </section>
  );
}
